<template>
  <b-sidebar
    id="sidebar-watchlist"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetAll"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0 text-capitalize">
          {{
            (isAdd ? $t("actions.add") : $t("actions.edit")) +
            " " +
            $tc("distribution_list.table_distribution.title", 1)
          }}
        </h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>

      <!-- BODY -->
      <!-- <div v-if="isSubmitting" class="d-flex align-items-center justify-content-center py-5">
        <b-icon
          icon="broadcast"
          animation="spin"
          font-scale="4"
          variant="primary"
        />
      </div> -->

      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
        >
          <!-- List Name -->
          <validation-provider 
          #default="validationContext" 
          name="name" 
          rules="required">
            <b-form-group
              class="text-capitalize"
              :label="$t('distribution_list.sidebar.list_name')"
              label-for="name"
            >
              <b-form-input
                id="name"
                v-model="itemData.name"
                :state="getValidationState(validationContext)"
                trim
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Users -->
          <validation-provider
            #default="validationContext"
            name="users"
            rules="required">
            <b-form-group 
              :label="$t('distribution_list.sidebar.users')"
              label-for="users">
              <v-select
                id="users"
                v-model="itemData.users"
                :options="userOptions"
                :multiple="true"
                :clearable="true"
                :reduce="option => option.id"
                :get-option-label="getUserLabel"
              />

              <b-form-invalid-feedback
                :state="getValidationState(validationContext)"
              >
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Emails -->
          <validation-provider
            #default="validationContext"
            name="emails"
          >
            <b-form-group
              :label="$t('distribution_list.sidebar.email')"
              label-for="emails"
            >
            <b-form-tags
              id="emails"
              v-model="itemData.emails"
              separator=",;"
              tag-variant="info"
              placeholder=""
              remove-on-delete
              no-outer-focus
              :tag-validator="isValidEmail"
              invalid-tag-text="@email.com"
            />
              <b-form-invalid-feedback
                :state="getValidationState(validationContext)"
              >
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
            >
              {{ isAdd ? $t("actions.add") : $t("actions.edit") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
            >
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { ref, computed } from "@vue/composition-api";
import { required } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    BFormCheckbox,
    vSelect,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
    users: {
      type: Array,
      default: () => [],
    },
    userOptions: {
      type: Array,
      default: null,
    },
  },
  setup(props, { emit }) {
    const { refFormObserver, getValidationState, resetForm } = formValidation();
    const { t } = useI18nUtils();
    const toast = useToast();
    // const isSubmitting = ref(false);

    const blankData = {
      name: "",
      users: [],
      emails: [],
    };

    const itemData = ref(JSON.parse(JSON.stringify(blankData)));

    const translations = {
      "distribution_list.table_distribution.successful_creation": t(
        "distribution_list.table_distribution.successful_creation"
      ),
      "distribution_list.table_distribution.error_creation": t(
        "distribution_list.table_distribution.error_creation"
      ),
      "distribution_list.table_distribution.successful_edit": t(
        "distribution_list.table_distribution.successful_edit"
      ),
      "distribution_list.table_distribution.error_edit": t("distribution_list.table_distribution.error_edit"),
    };

    const loadData = () => {
      if (props.isAdd) {
        itemData.value = JSON.parse(JSON.stringify(blankData));
      } else {
        itemData.value = {
          ...JSON.parse(JSON.stringify(blankData)),
          ...props.itemEdit,
          users: props.itemEdit.users
            ? props.itemEdit.users.map(u => u.id)
            : [],
        };
      }
    };


    const resetAll = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
      if (refFormObserver.value) {
        refFormObserver.value.reset();
      }
    };

    const onSubmit = async () => {
      // isSubmitting.value = true;
      try {
        const payload = {
          name: itemData.value.name,
          users: itemData.value.users,
          emails: itemData.value.emails,
        };

        let response;

        if (props.isAdd) {
          response = await store.dispatch(
            "distribution_list/add",
            payload
          );
        } else {
          response = await store.dispatch(
            "distribution_list/edit",
            {
              id: itemData.value.id,
              params: payload,
            }
          );
        }

        if (!response || !response.id) {
          throw new Error("Invalid response");
        }

        toast({
          component: ToastificationContent,
          props: {
            title:
              response.message ||
              (props.isAdd
                ? translations["distribution_list.table_distribution.successful_creation"]
                : translations["distribution_list.table_distribution.successful_edit"]),
            icon: "CheckIcon",
            variant: "success",
          },
        });

        emit("refetch-data");
        emit("update:is-sidebar-active", false);

      } catch (error) {
        console.error("Submit error:", error);

        toast({
          component: ToastificationContent,
          props: {
            title: props.isAdd
              ? translations["distribution_list.table_distribution.error_creation"]
              : translations["distribution_list.table_distribution.error_edit"],
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        // isSubmitting.value = false;
      }
    };

    const getUserLabel = (user) => {
      if (!user) return "";
      return `${user.username} | ${user.full_name}`;
    };

    const isValidEmail = (email) => {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    return {
      refFormObserver,
      getValidationState,
      itemData,
      loadData,
      resetAll,
      onSubmit,
      required,
      isValidEmail,
      getUserLabel,
      // isSubmitting,
    };
  },
};
</script>
